import { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { API_PREFIX } from '@/lib/api-client';

// Recreated (simplified) login: single-step email + password, OR Microsoft (Entra ID) SSO when the
// backend reports SSO is configured — the two are exclusive: with SSO on, the password form is
// hidden entirely (SSO-only deployments provision no LOCAL account). Keeps the uniticket glass
// design tokens (animated-gradient-bg / glass-card-login / glass-input).
export function Login() {
  const { state, login, mode } = useAuth();
  const { t, i18n } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // null = status unknown (fetch in flight) → render neither form nor button, avoiding a
  // password-form flash on SSO-only deployments.
  const [ssoEnabled, setSsoEnabled] = useState<boolean | null>(null);

  // Is Entra SSO configured? The status route only exists while SSO is on (router is gated), so a
  // failed/!ok fetch simply means "no SSO" → keep the button hidden.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_PREFIX}/auth/sso/status`)
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((d) => { if (!cancelled) setSsoEnabled(Boolean(d?.enabled)); })
      .catch(() => { if (!cancelled) setSsoEnabled(false); });
    return () => { cancelled = true; };
  }, []);

  // SSO failures redirect back here as ?sso_error=<code>; map it to a localized message.
  const ssoErrorCode = searchParams.get('sso_error');
  const ssoErrorMsg = ssoErrorCode
    ? t([`login.ssoError.${ssoErrorCode}`, 'login.ssoError.generic'])
    : '';

  // Already authenticated → into the app.
  if (state.status === 'authenticated') {
    return <Navigate to="/address-validations" replace />;
  }

  // Proxy (forward-auth) mode has no in-app login — the proxy authenticates. Bounce to the app (which
  // shows the access-denied screen if this Entra user isn't provisioned here).
  if (mode === 'proxy') {
    return <Navigate to="/" replace />;
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError(t('login.errorBothFields'));
      return;
    }
    setIsLoading(true);
    try {
      await login(email, password);
      // The AuthProvider state change redirects via ProtectedRoute / the guard above.
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('login.errorGeneric'));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-dvh animated-gradient-bg relative flex">
      {/* Top-left: Unifiber logo */}
      <div className="absolute top-4 left-4 sm:top-8 sm:left-10 z-10">
        <img src="/UnifiberLogoWhite.png" alt="Unifiber" className="h-8 sm:h-12 opacity-90" />
      </div>

      {/* Left side: App name (desktop only) */}
      <div className="hidden lg:flex lg:w-[50%] relative z-10 flex-col justify-center items-start pl-20 pr-12">
        <img src="/logo-av.png" alt="" className="w-24 h-24 mb-7 drop-shadow-2xl" />
        <h1 className="text-white text-6xl font-bold tracking-tight drop-shadow-lg leading-tight">
          Address<br />Validation
        </h1>
      </div>

      {/* Right side: Glass login card */}
      <div className="flex-1 flex items-start sm:items-center justify-center relative z-10 px-6 pt-20 pb-6 sm:py-12">
        <div className="glass-card-login rounded-2xl p-6 sm:p-10 w-full max-w-md space-y-6">
          {/* Mobile title */}
          <div className="lg:hidden text-center mb-4">
            <img src="/logo-av.png" alt="" className="w-14 h-14 mx-auto mb-3 drop-shadow-lg" />
            <h2 className="text-3xl font-bold gradient-text">Address Validation</h2>
          </div>

          {/* Header */}
          <div>
            <h2 className="text-xl font-bold text-white">{t('login.title')}</h2>
            <p className="text-white/60 mt-1 text-sm">{t('login.subtitle')}</p>
          </div>

          {/* Language selector */}
          <div className="flex items-center gap-1 bg-white/[0.08] rounded-lg p-1 w-fit border border-white/[0.15]">
            {['EN', 'FR', 'NL'].map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => i18n.changeLanguage(l.toLowerCase())}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  i18n.language.startsWith(l.toLowerCase())
                    ? 'bg-white/20 text-white'
                    : 'text-white/60 hover:text-white/90'
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          {/* SSO failure banner (from ?sso_error= redirect) */}
          {ssoErrorMsg && (
            <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
              {ssoErrorMsg}
            </div>
          )}

          {/* Password login form — only when SSO is NOT configured (exclusive paths) */}
          {ssoEnabled === false && (
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-1.5">
                {t('login.emailLabel')}
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                disabled={isLoading}
                className="glass-input w-full px-4 py-2.5 rounded-lg text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-1.5">
                {t('login.passwordLabel')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.passwordPlaceholder')}
                  disabled={isLoading}
                  className="glass-input w-full px-4 py-2.5 rounded-lg text-sm pr-12 text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose hover:-translate-y-px hover:shadow-[0_8px_25px_rgba(237,18,95,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
            >
              {isLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {isLoading ? t('login.signingIn') : t('login.signIn')}
            </button>
          </form>
          )}

          {/* Microsoft (Entra ID) SSO — only when configured; replaces the password form */}
          {ssoEnabled === true && (
              <button
                type="button"
                onClick={() => { window.location.href = `${API_PREFIX}/auth/sso/login`; }}
                className="w-full py-2.5 rounded-lg text-white/90 font-semibold text-sm bg-white/[0.08] border border-white/[0.15] hover:bg-white/[0.14] transition-colors flex items-center justify-center gap-2.5"
              >
                <svg viewBox="0 0 21 21" className="w-4 h-4" aria-hidden="true">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                {t('login.ssoButton')}
              </button>
          )}
        </div>
      </div>

      {/* Bottom copyright */}
      <div className="absolute bottom-6 left-0 right-0 text-center z-10">
        <p className="text-white/25 text-xs">{t('footer.copyright', { year: new Date().getFullYear() })}</p>
      </div>
    </div>
  );
}
